import { useAnalytics } from '@inflearn/analytics';
import { SkeletonHeader } from '@inflearn/app-shell/dist/skeleton';
import { Box } from '@inflearn/ds-react-next';
import { useLang } from '@inflearn/i18n-tools';
import dynamic from 'next/dynamic';
import { createContext, useState } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';

import { useLoginModalContext } from '../../providers/ControlLoginModalContextProvider';
import { queryClient } from '../../providers/QueryClientProvider';
import { getCurrentLanguage } from '../../utils/i18n/getCurrentLanguage';

const AppShellProvider = dynamic(() => import('appShell/AppShellProvider'), {
  ssr: false,
  loading: () => <SkeletonHeader lang={getCurrentLanguage()} />, // NOTE: provider -> app-shell -> header 순으로 전부 로딩 될 때 까지 skeleton header 를 보여준다.
});
const AppShell = dynamic(() => import('appShell/AppShell'), {
  ssr: false,
  loading: () => <SkeletonHeader lang={getCurrentLanguage()} />,
});
const AppShellHeader = dynamic(() => import('appShell/Header'), {
  ssr: false,
  loading: () => <SkeletonHeader lang={getCurrentLanguage()} />,
});
const AppShellFooter = dynamic(() => import('appShell/Footer'), {
  ssr: false,
});
const LoginModal = dynamic(() => import('appShell/LoginModal'), {
  ssr: false,
});

type Props = {
  children: ReactNode;
  showChanneltalk?: boolean;
  footer?: {
    isVisible?: boolean;
    hasBottomNavigation?: boolean;
    hasFNB?: boolean;
  };
};

const defaultFooterProps = {
  isVisible: true,
  hasBottomNavigation: true,
  hasFNB: false,
};

export default function AppShellLayoutForSSR({
  children,
  showChanneltalk = true,
  footer,
}: Readonly<Props>) {
  const { analytics } = useAnalytics();
  const { close, isOpened } = useLoginModalContext();
  const [isAppShellSticky, setIsAppShellSticky] = useState(true);

  const footerProps = { ...defaultFooterProps, ...footer };
  const { isKo } = useLang();

  return (
    <AppShellStickyProvider
      isAppShellSticky={isAppShellSticky}
      setIsAppShellSticky={setIsAppShellSticky}
    >
      <AppShellProvider queryClient={queryClient} analytics={analytics}>
        <AppShell
          header={<AppShellHeader disabledSticky={!isAppShellSticky} />}
          disableBottomNavigation={!footerProps.hasBottomNavigation}
          showChanneltalk={isKo ? showChanneltalk : false}
        >
          <LoginModal onClose={close} opened={isOpened} />
        </AppShell>
      </AppShellProvider>
      <Box component="main">{children}</Box>
      {footerProps.isVisible ? <AppShellFooter hasFNB={footerProps.hasFNB} /> : null}
    </AppShellStickyProvider>
  );
}

type AppShellStickyContextProps = {
  isAppShellSticky: boolean;
  setIsAppShellSticky: Dispatch<SetStateAction<boolean>>;
};

export const AppShellStickyContext = createContext<AppShellStickyContextProps | null>(null);

export const AppShellStickyProvider = ({
  isAppShellSticky,
  setIsAppShellSticky,
  children,
}: {
  isAppShellSticky: boolean;
  setIsAppShellSticky: Dispatch<SetStateAction<boolean>>;
  children: ReactNode;
}) => (
  <AppShellStickyContext.Provider
    value={{
      isAppShellSticky,
      setIsAppShellSticky,
    }}
  >
    {children}
  </AppShellStickyContext.Provider>
);
